import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, CreditCard, MessageCircle, CalendarClock, BookOpen, Megaphone, Award } from "lucide-react";
import { api } from "@/lib/apiClient";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";

const ICONS = {
  payment: CreditCard, message: MessageCircle, mentorship: CalendarClock,
  review: BookOpen, announcement: Megaphone, badge: Award,
};

function timeAgo(iso) {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}

export default function NotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data.items || []); setUnread(data.unread || 0); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const markAll = async () => { setUnread(0); setItems((xs) => xs.map((n) => ({ ...n, read: true }))); try { await api.post("/notifications/read-all"); } finally { load(); } };
  const click = async (n) => { if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch {} } if (n.link) nav(n.link); load(); };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button className="relative w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="notif-bell" aria-label="Notifications">
          <Bell className="w-5 h-5 text-white/80" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange text-white text-[10px] font-bold flex items-center justify-center shadow" data-testid="notif-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <span className="font-heading font-semibold text-sm">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs font-medium text-teal hover:underline flex items-center gap-1" data-testid="notif-mark-all">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notif-empty">
              <Bell className="w-8 h-8 mx-auto mb-2 text-border" />
              You're all caught up!
            </div>
          ) : items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => click(n)} data-testid={`notif-item-${n.id}`}
                className={`w-full text-left flex gap-3 px-3 py-3 border-b border-border/60 last:border-0 hover:bg-secondary/60 transition-colors ${n.read ? "" : "bg-teal/5"}`}>
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${n.read ? "bg-secondary text-muted-foreground" : "bg-teal/15 text-teal"}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="font-medium text-sm text-navy truncate">{n.title}</span>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-orange shrink-0" />}
                  </span>
                  {n.body && <span className="block text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</span>}
                  <span className="block text-[11px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</span>
                </span>
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
